import { useState, useEffect, useCallback } from 'react';
import noteService from '../services/noteService';
import { notify } from '../components/CustomToast';

export const useNotes = (initialFilters = {}) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState(initialFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const fetchNotes = useCallback(async (page = 1, isLoadMore = false) => {
        try {
            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const params = { ...filters, per_page: 12, page };
            const response = await noteService.list(params);
            const newNotes = (response?.status && response?.data?.notes) ? response.data.notes : [];

            if (isLoadMore) {
                setNotes(prev => [...prev, ...newNotes]);
            } else {
                setNotes(newNotes);
            }

            if (response?.data?.pagination) {
                const { current_page, last_page } = response.data.pagination;
                setCurrentPage(current_page);
                setHasMore(current_page < last_page);
            } else {
                setHasMore(false);
            }

            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to fetch notes');
            notify('error', 'Failed to load notes');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchNotes(1, false);
    }, [fetchNotes]);

    const loadMore = useCallback(() => {
        if (!loadingMore && hasMore) {
            fetchNotes(currentPage + 1, true);
        }
    }, [loadingMore, hasMore, currentPage, fetchNotes]);

    const refetch = useCallback(() => {
        setCurrentPage(1);
        setHasMore(true);
        return fetchNotes(1, false);
    }, [fetchNotes]);

    const updateFilters = (newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    };

    return {
        notes,
        loading,
        loadingMore,
        error,
        hasMore,
        loadMore,
        refetch,
        updateFilters,
        filters,
    };
};

export const useNote = (noteId) => {
    const [note, setNote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchNote = useCallback(async () => {
        if (!noteId) return;

        try {
            setLoading(true);
            const response = await noteService.show(noteId);
            setNote(response?.data?.note || null);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to fetch note');
            notify('error', 'Failed to load note details');
        } finally {
            setLoading(false);
        }
    }, [noteId]);

    useEffect(() => {
        fetchNote();
    }, [fetchNote]);

    const updateNote = async (data) => {
        try {
            const response = await noteService.update(noteId, data);
            setNote(response?.data?.note || null);
            notify('success', 'Note updated successfully');
            return response?.data?.note;
        } catch (err) {
            notify('error', err.message || 'Failed to update note');
            throw err;
        }
    };

    const deleteNote = async () => {
        try {
            await noteService.remove(noteId);
            notify('delete', 'Note deleted successfully');
            return true;
        } catch (err) {
            notify('error', err.message || 'Failed to delete note');
            throw err;
        }
    };

    return {
        note,
        loading,
        error,
        refetch: fetchNote,
        updateNote,
        deleteNote,
    };
};
